/* Shipping insurance toggle for the Rebuy Smart Cart.
   Picks the tier variant from the cart value (excluding the insurance line itself), keeps it
   in sync when the cart changes, and removes it when the cart has nothing else in it.
   The insurance product carries the Rebuy tags `smart-cart-hidden` (no line item, not counted
   as an item) and `smart-cart-shipping-excluded` (doesn't count toward free shipping). */
(function () {
  'use strict';

  // USD thresholds in cents: [upper bound (exclusive), SKU].
  var TIERS = [
    { below: 5000, sku: 'SHIP-INS-1' },
    { below: 10000, sku: 'SHIP-INS-2' },
    { below: 50001, sku: 'SHIP-INS-3' },
    { below: Infinity, sku: 'SHIP-INS-4' }
  ];
  var REF_TIER3_USD_CENTS = 399; // USD price of SHIP-INS-3, used to scale thresholds to other currencies
  var ANCHOR = '.rebuy-cart__flyout-subtotal'; // the root is inserted just above the subtotal

  var root, toggle, priceEl, errorEl, config;
  var busy = false;
  var timer = null;
  var latestCart = null;
  var OPT_OUT_KEY = 'si_opt_out';

  function optedOut() {
    try { return localStorage.getItem(OPT_OUT_KEY) === '1'; } catch (e) { return false; }
  }
  function setOptedOut(v) {
    try { if (v) localStorage.setItem(OPT_OUT_KEY, '1'); else localStorage.removeItem(OPT_OUT_KEY); } catch (e) {}
  }

  function variantBySku(sku) {
    for (var i = 0; i < config.variants.length; i++) if (config.variants[i].sku === sku) return config.variants[i];
    return null;
  }

  function neededVariant(baseCents) {
    var tier3 = variantBySku('SHIP-INS-3');
    var rate = tier3 ? tier3.cents / REF_TIER3_USD_CENTS : 1;
    var usd = baseCents / rate;
    for (var i = 0; i < TIERS.length; i++) {
      if (usd < TIERS[i].below) return variantBySku(TIERS[i].sku);
    }
    return null;
  }

  function post(url, body) {
    return fetch(window.Shopify.routes.root + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }

  function getCart() {
    return fetch(window.Shopify.routes.root + 'cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); });
  }

  function split(cart) {
    var base = 0, line = null;
    cart.items.forEach(function (it) {
      if (it.handle === config.handle) line = it;
      else base += it.final_line_price;
    });
    return { base: base, line: line };
  }

  function paint(cart) {
    latestCart = cart;
    var parts = split(cart);
    var need = neededVariant(parts.base);
    if (parts.base === 0 || !need) {
      root.hidden = true;
      return;
    }
    priceEl.textContent = need.money;
    toggle.checked = !!parts.line;
    root.hidden = false;
  }

  // Make the cart match the toggle and the current tier. Resolves to the final cart.
  function reconcile(cart, wantOn) {
    var parts = split(cart);
    var need = neededVariant(parts.base);
    var on = wantOn === undefined ? !!parts.line : wantOn;
    // On by default (outside the EU/EEA) until the customer switches it off.
    if (wantOn === undefined && !parts.line && config.defaultOn && !optedOut()) on = true;
    if (parts.base === 0 || !need) {
      on = false;
      setOptedOut(false); // empty cart: next cart starts with the default again
    }

    var stale = parts.line && (!on || parts.line.variant_id !== need.id || parts.line.quantity !== 1);
    var missing = on && (!parts.line || stale);
    if (!stale && !missing) return Promise.resolve(cart);

    var chain = Promise.resolve();
    if (stale) chain = chain.then(function () { return post('cart/change.js', { id: String(parts.line.key), quantity: 0 }); });
    if (missing) chain = chain.then(function () { return post('cart/add.js', { id: need.id, quantity: 1 }); });
    return chain.then(getCart);
  }

  // Ask Rebuy to re-read the Shopify cart so its line items and subtotal update.
  function refreshRebuy() {
    var R = window.Rebuy;
    try {
      if (R && R.Cart && typeof R.Cart.fetchShopifyCart === 'function') return R.Cart.fetchShopifyCart();
      if (R && R.SmartCart && typeof R.SmartCart.render === 'function') return R.SmartCart.render();
    } catch (e) { console.warn('Shipping insurance: Rebuy refresh failed', e); }
  }

  function run(wantOn) {
    if (busy) return Promise.resolve();
    busy = true;
    root.classList.add('is-busy');
    errorEl.hidden = true;
    return getCart()
      .then(function (cart) {
        return reconcile(cart, wantOn).then(function (after) {
          paint(after);
          if (after !== cart) refreshRebuy();
        });
      })
      .catch(function (e) {
        console.error('Shipping insurance:', e);
        errorEl.hidden = false;
        return getCart().then(paint).catch(function () {});
      })
      .then(function () {
        busy = false;
        root.classList.remove('is-busy');
      });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(function () { if (!busy) run(); }, 250);
  }

  // Rebuy rebuilds its drawer markup; keep our block just above the subtotal.
  function setCartOpen(open) {
    document.documentElement.classList.toggle('si-cart-open', !!open);
  }

  function syncCartOpen() {
    var R = window.Rebuy;
    if (R && R.SmartCart && typeof R.SmartCart.visible === 'boolean') setCartOpen(R.SmartCart.visible);
  }

  function mount() {
    syncCartOpen();
    var anchor = document.querySelector(ANCHOR);
    if (!anchor || !anchor.parentNode) return;
    // Climb out of any horizontal flex row so the block sits above the subtotal row, full width.
    var target = anchor;
    for (var i = 0; i < 4 && target.parentElement; i++) {
      var cs = window.getComputedStyle(target.parentElement);
      if (cs.display.indexOf('flex') !== -1 && cs.flexDirection.indexOf('row') === 0) target = target.parentElement;
      else break;
    }
    if (root.nextElementSibling !== target || root.parentNode !== target.parentNode) {
      target.parentNode.insertBefore(root, target);
      if (latestCart) paint(latestCart);
      else schedule();
    }
  }

  function init() {
    root = document.querySelector('[data-si-root]');
    var cfgEl = document.querySelector('[data-si-config]');
    if (!root || !cfgEl) return;
    try { config = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    toggle = root.querySelector('[data-si-toggle]');
    priceEl = root.querySelector('[data-si-price]');
    errorEl = root.querySelector('[data-si-error]');

    toggle.addEventListener('change', function () { setOptedOut(!toggle.checked); run(toggle.checked); });

    ['rebuy:cart.ready', 'rebuy:cart.change', 'rebuy:smartcart.ready', 'rebuy:smartcart.show'].forEach(function (name) {
      document.addEventListener(name, function () { mount(); schedule(); });
    });

    var mo = null;
    new MutationObserver(function () {
      if (mo) return;
      mo = setTimeout(function () { mo = null; mount(); }, 100);
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener('rebuy:smartcart.show', function () { setCartOpen(true); });
    document.addEventListener('rebuy:smartcart.hide', function () { setCartOpen(false); });

    mount();
    schedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
